import React, { forwardRef } from "react";
import pnoeLogo from "../../common/images/logo.png";
import {
  AiSummaryMetric,
  AiSummaryResponse,
  AiSummarySystem,
  AiSummaryTakeawayItem,
} from "./aiSummaryTypes";
import { AiRmrOverviewMetric, AiRmrSummary } from "./aiRmrSummaryTypes";
import { AiAmrOverviewMetric, AiAmrSummary } from "./aiAmrSummaryTypes";
import { AiRmrInBodySummary } from "./aiRmrInBodySummaryTypes";
import AiRmrInBodySummaryView from "./AiRmrInBodySummaryView";
import AiProgressSummaryView from "./AiProgressSummaryView";
import AiBloodProgressSummaryView from "./AiBloodProgressSummaryView";
import { AiBloodProgressSummary } from "./aiBloodProgressSummaryResponse";
import { AiProgressSummary } from "./aiProgressSummaryTypes";
import { AMR_METRIC_GROUP_LABELS } from "./aiAmrSummaryPresentation";
import { AiSummaryKind } from "./aiSummaryKinds";
import AiAmrZonePanels from "./AiAmrZonePanels";
import AiSummaryBiologicalAgeCard from "./AiSummaryBiologicalAgeCard";
import AiSummaryRecommendationsView, {
  hasAiSummaryRecommendations,
} from "./AiSummaryRecommendationsView";
import classes from "./AiSummaryPdfDocument.module.css";

type Props = {
  kind: AiSummaryKind;
  healthSummary: AiSummaryResponse | null;
  rmrSummary: AiRmrSummary | null;
  rmrInBodySummary: AiRmrInBodySummary | null;
  amrSummary: AiAmrSummary | null;
  progressSummary?: AiProgressSummary | null;
  bloodProgressSummary?: AiBloodProgressSummary | null;
  style?: React.CSSProperties;
};

type Tone = "positive" | "attention" | "neutral" | "warning";

const RMR_METRIC_GROUPS: Record<string, string> = {
  "3_RMR": "Mitochondria",
  "4_RMR": "Heart",
  "8_RMR": "Mitochondria",
  "11_RMR": "Metabolism",
  "13_RMR": "Respiratory function",
  "15_RMR": "Respiratory function",
  "19_RMR": "Posture",
  "25_RMR": "Nervous system",
  "26_RMR": "Lung",
};

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, value));

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

const formatDate = (value: string): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatMeasurementDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatLabel = (value: string): string =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const toneClass: Record<Tone, string> = {
  positive: classes.tonePositive,
  attention: classes.toneAttention,
  neutral: classes.toneNeutral,
  warning: classes.toneWarning,
};

const getRmrMetricTone = (scoreCategory?: number): Tone => {
  if (scoreCategory === 1) {
    return "attention";
  }
  if (scoreCategory === 2) {
    return "warning";
  }
  if (scoreCategory === 4 || scoreCategory === 5) {
    return "positive";
  }
  return "neutral";
};

const ReportHeader = ({
  title,
  subtitle,
  meta,
}: {
  title: string;
  subtitle: string;
  meta?: React.ReactNode;
}) => (
  <header className={classes.reportHeader} data-pdf-break="after">
    <div className={classes.brandBlock}>
      <img src={pnoeLogo} alt="PNOE" />
      <span>Clinical Intelligence</span>
    </div>
    <div className={classes.reportIdentity}>
      <p>{subtitle}</p>
      <h1>{title}</h1>
      {meta && <div className={classes.reportMeta}>{meta}</div>}
    </div>
  </header>
);

const SectionTitle = ({
  index,
  title,
  tone = "neutral",
}: {
  index?: number;
  title: string;
  tone?: Tone;
}) => (
  <div className={`${classes.sectionTitle} ${toneClass[tone]}`}>
    {index !== undefined && <span>{index}</span>}
    <h2>{title}</h2>
    <i />
  </div>
);

const HealthMeasurementTable = ({ metric }: { metric: AiSummaryMetric }) => {
  if (!metric.showMeasurementTable || !metric.measurements?.length) {
    return null;
  }

  const measurements = metric.measurements.slice(0, 2);
  return (
    <table className={classes.healthMeasurementTable}>
      <thead>
        <tr>
          {measurements.map((measurement, index) => (
            <th key={`${measurement.dateCreated}-${index}`}>
              {formatMeasurementDate(measurement.dateCreated)}
            </th>
          ))}
          {metric.interpretation && <th>Interpretation</th>}
        </tr>
      </thead>
      <tbody>
        <tr>
          {measurements.map((measurement, index) => (
            <td key={`${measurement.value}-${index}`}>
              {formatNumber(measurement.value)}
              {metric.unit ? ` ${metric.unit}` : ""}
            </td>
          ))}
          {metric.interpretation && (
            <td>
              <strong>{metric.interpretation}</strong>
            </td>
          )}
        </tr>
      </tbody>
    </table>
  );
};

const HealthMetricCard = ({
  metric,
  tone,
}: {
  metric: AiSummaryMetric;
  tone: "positive" | "attention";
}) => (
  <article className={`${classes.healthMetricCard} ${toneClass[tone]}`}>
    <div className={classes.healthMetricHeader}>
      <h4>{metric.title}</h4>
      {metric.scoreCategory && <span>{metric.scoreCategory}</span>}
    </div>
    {metric.scorePercent !== undefined && (
      <div className={classes.scoreRow}>
        <div className={classes.scoreTrack}>
          <i style={{ width: `${clampPercent(metric.scorePercent)}%` }} />
        </div>
        <strong>{formatNumber(metric.scorePercent)}%</strong>
      </div>
    )}
    {metric.bullets.length > 0 && (
      <ul>
        {metric.bullets.map((bullet, index) => (
          <li key={`${metric.name}-pdf-bullet-${index}`}>{bullet}</li>
        ))}
      </ul>
    )}
    <HealthMeasurementTable metric={metric} />
  </article>
);

const HealthMetricRows = ({
  metrics,
  tone,
}: {
  metrics: AiSummaryMetric[];
  tone: "positive" | "attention";
}) => (
  <div className={classes.healthMetricRows}>
    {chunk(metrics, 2).map((row, rowIndex) => (
      <div
        key={`health-metric-row-${rowIndex}`}
        className={classes.healthMetricRow}
        data-pdf-break="after"
      >
        {row.map((metric, metricIndex) => (
          <HealthMetricCard
            key={`${metric.name}-${metricIndex}`}
            metric={metric}
            tone={tone}
          />
        ))}
      </div>
    ))}
  </div>
);

const HealthSystem = ({
  system,
  defaultTone,
}: {
  system: AiSummarySystem;
  defaultTone: "positive" | "attention";
}) => {
  const hasHighlights = system.highlights.length > 0;
  const hasLowlights = system.lowlights.length > 0;

  return (
    <article className={classes.healthSystem} data-pdf-break="after">
      <div className={classes.healthSystemHeader}>
        <div>
          <span>Health system</span>
          <h3>{formatLabel(system.name)}</h3>
        </div>
        {(system.score !== undefined || system.scoreCategory) && (
          <div className={classes.healthSystemScore}>
            {system.score !== undefined && (
              <strong>{formatNumber(system.score)}</strong>
            )}
            {system.scoreCategory && <span>{system.scoreCategory}</span>}
          </div>
        )}
      </div>
      {system.score !== undefined && (
        <div
          className={`${classes.systemScoreTrack} ${toneClass[defaultTone]}`}
        >
          <i style={{ width: `${clampPercent(system.score)}%` }} />
        </div>
      )}
      {hasHighlights && (
        <div className={classes.healthMetricCollection}>
          {hasLowlights && <h4>Highlights</h4>}
          <HealthMetricRows metrics={system.highlights} tone="positive" />
        </div>
      )}
      {hasLowlights && (
        <div className={classes.healthMetricCollection}>
          {hasHighlights && <h4>Needs attention</h4>}
          <HealthMetricRows metrics={system.lowlights} tone="attention" />
        </div>
      )}
    </article>
  );
};

const TakeawayItems = ({
  title,
  items,
  tone,
}: {
  title: string;
  items: AiSummaryTakeawayItem[];
  tone: "positive" | "attention";
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className={`${classes.takeawayGroup} ${toneClass[tone]}`}>
      <h3>{title}</h3>
      {items.map((item, index) => (
        <article key={`${title}-${index}`}>
          {item.title && <h4>{item.title}</h4>}
          {item.text && <p>{item.text}</p>}
          {item.points && item.points.length > 0 && (
            <ul>
              {item.points.map((point, pointIndex) => (
                <li key={`${title}-${index}-${pointIndex}`}>{point}</li>
              ))}
            </ul>
          )}
        </article>
      ))}
    </section>
  );
};

const HealthOverviewPdf = ({ summary }: { summary: AiSummaryResponse }) => {
  const hasRecommendations = hasAiSummaryRecommendations({
    trainingPlan: summary.trainingPlan,
    nutrition: summary.nutrition,
    recommendations: summary.recommendations,
  });
  const hasTakeaway = Boolean(
    summary.keyTakeaway.overallPicture ||
      summary.keyTakeaway.highlightsSummary.length > 0 ||
      summary.keyTakeaway.lowlightsSummary.length > 0
  );
  let sectionIndex = 0;

  return (
    <>
      <ReportHeader
        title="Health Overview"
        subtitle="AI-generated clinical summary"
        meta={<span>Patient health intelligence</span>}
      />

      {hasTakeaway && (
        <section className={classes.reportSection}>
          <SectionTitle index={(sectionIndex += 1)} title="Key takeaway" />
          {summary.keyTakeaway.overallPicture && (
            <div className={classes.takeawayHero} data-pdf-break="after">
              <span>Overall picture</span>
              <p>{summary.keyTakeaway.overallPicture}</p>
            </div>
          )}
          <div className={classes.takeawayColumns} data-pdf-break="after">
            <TakeawayItems
              title="What is working well"
              items={summary.keyTakeaway.highlightsSummary}
              tone="positive"
            />
            <TakeawayItems
              title="What needs attention"
              items={summary.keyTakeaway.lowlightsSummary}
              tone="attention"
            />
          </div>
        </section>
      )}

      {summary.highlights.length > 0 && (
        <section className={classes.reportSection}>
          <SectionTitle
            index={(sectionIndex += 1)}
            title="Highlights"
            tone="positive"
          />
          <div className={classes.healthSystems}>
            {summary.highlights.map((system, index) => (
              <HealthSystem
                key={`${system.name}-highlight-${index}`}
                system={system}
                defaultTone="positive"
              />
            ))}
          </div>
        </section>
      )}

      {summary.lowlights.length > 0 && (
        <section className={classes.reportSection}>
          <SectionTitle
            index={(sectionIndex += 1)}
            title="Lowlights"
            tone="attention"
          />
          <div className={classes.healthSystems}>
            {summary.lowlights.map((system, index) => (
              <HealthSystem
                key={`${system.name}-lowlight-${index}`}
                system={system}
                defaultTone="attention"
              />
            ))}
          </div>
        </section>
      )}

      {hasRecommendations && (
        <section className={classes.reportSection}>
          <SectionTitle index={(sectionIndex += 1)} title="Recommendations" />
          <AiSummaryRecommendationsView
            trainingPlan={summary.trainingPlan}
            nutrition={summary.nutrition}
            recommendations={summary.recommendations}
            variant="pdf"
          />
        </section>
      )}

      {summary.disclaimer && (
        <section className={classes.disclaimer} data-pdf-break="after">
          <strong>Disclaimer</strong>
          <p>{summary.disclaimer}</p>
        </section>
      )}
    </>
  );
};

const RmrValuePdfCard = ({ summary }: { summary: AiRmrSummary }) => {
  if (!summary.hero.rmr) {
    return null;
  }

  return (
    <article className={classes.rmrHeroCard}>
      <p className={classes.cardEyebrow}>Resting metabolic rate</p>
      <div className={classes.rmrHeadline}>
        <strong>{formatNumber(summary.hero.rmr.value)}</strong>
        {summary.hero.rmr.unit && <span>{summary.hero.rmr.unit}</span>}
      </div>
      {summary.rmrDescription && <p>{summary.rmrDescription}</p>}
    </article>
  );
};

const FuelSourcePdfCard = ({ summary }: { summary: AiRmrSummary }) => {
  const fatPercent = summary.hero.fatPercent;
  const carbPercent = summary.hero.carbPercent;
  if (fatPercent === undefined && carbPercent === undefined) {
    return null;
  }

  const fat = clampPercent(fatPercent ?? 0);
  const carbs = clampPercent(carbPercent ?? 0);
  const total = fat + carbs || 1;

  return (
    <article className={classes.rmrHeroCard}>
      <p className={classes.cardEyebrow}>Fuel sources at rest</p>
      <div className={classes.fuelBar}>
        <i style={{ width: `${(fat / total) * 100}%` }} />
        <b style={{ width: `${(carbs / total) * 100}%` }} />
      </div>
      <div className={classes.fuelLegend}>
        {fatPercent !== undefined && (
          <span>Fats {formatNumber(fatPercent)}%</span>
        )}
        {carbPercent !== undefined && (
          <span>Carbs {formatNumber(carbPercent)}%</span>
        )}
      </div>
      {summary.fuelDescription && <p>{summary.fuelDescription}</p>}
    </article>
  );
};

const RmrMetricCard = ({ metric }: { metric: AiRmrOverviewMetric }) => {
  const progress = clampPercent(metric.value);
  const tone = getRmrMetricTone(metric.scoreCategory);

  return (
    <article className={`${classes.rmrMetricCard} ${toneClass[tone]}`}>
      <div className={classes.rmrScoreRing}>
        <svg viewBox="0 0 44 44" aria-hidden="true">
          <circle cx="22" cy="22" r="18" pathLength="100" />
          <circle
            cx="22"
            cy="22"
            r="18"
            pathLength="100"
            strokeDasharray={`${progress} ${100 - progress}`}
          />
        </svg>
        <strong>
          {formatNumber(metric.value)}
          {metric.unit || "%"}
        </strong>
      </div>
      <div>
        <p>{RMR_METRIC_GROUPS[metric.id] || "Health metric"}</p>
        <h3>{metric.label}</h3>
        {metric.scoreCategoryLabel && <span>{metric.scoreCategoryLabel}</span>}
      </div>
    </article>
  );
};

const AmrMetricCard = ({ metric }: { metric: AiAmrOverviewMetric }) => {
  const progress = clampPercent(metric.value);
  const tone = getRmrMetricTone(metric.scoreCategory);

  return (
    <article className={`${classes.rmrMetricCard} ${toneClass[tone]}`}>
      <div className={classes.rmrScoreRing}>
        <svg viewBox="0 0 44 44" aria-hidden="true">
          <circle cx="22" cy="22" r="18" pathLength="100" />
          <circle
            cx="22"
            cy="22"
            r="18"
            pathLength="100"
            strokeDasharray={`${progress} ${100 - progress}`}
          />
        </svg>
        <strong>
          {formatNumber(metric.value)}
          {metric.unit || "%"}
        </strong>
      </div>
      <div>
        <p>{AMR_METRIC_GROUP_LABELS[metric.id] || "Health metric"}</p>
        <h3>{metric.label}</h3>
        {metric.scoreCategoryLabel && <span>{metric.scoreCategoryLabel}</span>}
      </div>
    </article>
  );
};

const RmrSummaryPdf = ({ summary }: { summary: AiRmrSummary }) => {
  const hasHero = Boolean(
    summary.hero.rmr ||
      summary.hero.fatPercent !== undefined ||
      summary.hero.carbPercent !== undefined ||
      summary.patient.biologicalAge !== undefined ||
      summary.patient.chronologicalAge !== undefined
  );
  const hasRecommendations = hasAiSummaryRecommendations({
    trainingPlan: summary.trainingPlan,
    nutrition: summary.nutrition,
    recommendations: summary.recommendations,
  });

  return (
    <>
      <ReportHeader
        title="RMR Summary"
        subtitle={summary.patient.companyName || "Metabolic assessment"}
        meta={
          <>
            {summary.patient.name && <strong>{summary.patient.name}</strong>}
            {summary.patient.testDate && (
              <span>Test date: {formatDate(summary.patient.testDate)}</span>
            )}
            {summary.reportType && <em>{summary.reportType}</em>}
            {summary.legend && <small>{summary.legend}</small>}
          </>
        }
      />

      {hasHero && (
        <section className={classes.rmrHeroGrid} data-pdf-break="after">
          <AiSummaryBiologicalAgeCard
            biologicalAge={summary.patient.biologicalAge}
            chronologicalAge={summary.patient.chronologicalAge}
            description={summary.bioAgeDescription}
            variant="pdf"
          />
          <RmrValuePdfCard summary={summary} />
          <FuelSourcePdfCard summary={summary} />
        </section>
      )}

      {summary.overviewMetrics.length > 0 && (
        <section className={classes.reportSection}>
          <SectionTitle title="Overview" />
          <div className={classes.rmrMetricRows}>
            {chunk(summary.overviewMetrics, 3).map((row, rowIndex) => (
              <div
                key={`rmr-metric-row-${rowIndex}`}
                className={classes.rmrMetricRow}
                data-pdf-break="after"
              >
                {row.map((metric) => (
                  <RmrMetricCard key={metric.id} metric={metric} />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {summary.measuredValues.length > 0 && (
        <section className={classes.measuredValues} data-pdf-break="after">
          <h2>Measured values</h2>
          <div>
            {summary.measuredValues.map((measurement) => (
              <article key={measurement.id}>
                <span>{measurement.label}</span>
                <strong>{formatNumber(measurement.value)}</strong>
                {measurement.unit && <small>{measurement.unit}</small>}
              </article>
            ))}
          </div>
        </section>
      )}

      {hasRecommendations && (
        <AiSummaryRecommendationsView
          trainingPlan={summary.trainingPlan}
          nutrition={summary.nutrition}
          recommendations={summary.recommendations}
          variant="pdf"
        />
      )}

      {summary.disclaimer && (
        <section className={classes.disclaimer} data-pdf-break="after">
          <strong>Disclaimer</strong>
          <p>{summary.disclaimer}</p>
        </section>
      )}
    </>
  );
};

const AmrSummaryPdf = ({ summary }: { summary: AiAmrSummary }) => {
  const vo2Peak = summary.measuredValues.find(
    (measurement) => measurement.id === "VO2PEAK"
  );
  const hasHero = Boolean(
    vo2Peak ||
      summary.patient.biologicalAge !== undefined ||
      summary.patient.chronologicalAge !== undefined
  );
  const hasRecommendations = hasAiSummaryRecommendations({
    trainingPlan: summary.trainingPlan,
    nutrition: summary.nutrition,
    recommendations: summary.recommendations,
  });

  return (
    <>
      <ReportHeader
        title="AMR Summary"
        subtitle={summary.patient.companyName || "Active metabolic assessment"}
        meta={
          <>
            {summary.patient.name && <strong>{summary.patient.name}</strong>}
            {summary.patient.testDate && (
              <span>Test date: {formatDate(summary.patient.testDate)}</span>
            )}
            {summary.reportType && <em>{summary.reportType}</em>}
            {summary.legend && <small>{summary.legend}</small>}
          </>
        }
      />

      {hasHero && (
        <section className={classes.amrHeroGrid} data-pdf-break="after">
          <AiSummaryBiologicalAgeCard
            biologicalAge={summary.patient.biologicalAge}
            chronologicalAge={summary.patient.chronologicalAge}
            description={summary.bioAgeDescription}
            variant="pdf"
          />
          {vo2Peak && (
            <article className={classes.rmrHeroCard}>
              <p className={classes.cardEyebrow}>VO2 Peak</p>
              <div className={classes.rmrHeadline}>
                <strong>{formatNumber(vo2Peak.value)}</strong>
                {vo2Peak.unit && <span>{vo2Peak.unit}</span>}
              </div>
              {summary.vo2PeakDescription && (
                <p>{summary.vo2PeakDescription}</p>
              )}
            </article>
          )}
        </section>
      )}

      {summary.overviewMetrics.length > 0 && (
        <section className={classes.reportSection}>
          <SectionTitle title="Overview" />
          <div className={classes.rmrMetricRows}>
            {chunk(summary.overviewMetrics, 3).map((row, rowIndex) => (
              <div
                key={`amr-metric-row-${rowIndex}`}
                className={classes.rmrMetricRow}
                data-pdf-break="after"
              >
                {row.map((metric) => (
                  <AmrMetricCard key={metric.id} metric={metric} />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {summary.measuredValues.length > 0 && (
        <section className={classes.measuredValues} data-pdf-break="after">
          <h2>Measured values</h2>
          <div>
            {summary.measuredValues.map((measurement) => (
              <article key={measurement.id}>
                <span>{measurement.label}</span>
                <strong>{formatNumber(measurement.value)}</strong>
                {measurement.unit && <small>{measurement.unit}</small>}
              </article>
            ))}
          </div>
        </section>
      )}

      {(summary.trainingZones.length > 0 ||
        summary.energyConsumption.length > 0) && (
        <section className={classes.reportSection}>
          <SectionTitle title="Training intensity & fuel use" />
          <AiAmrZonePanels
            trainingZones={summary.trainingZones}
            energyConsumption={summary.energyConsumption}
            variant="pdf"
          />
        </section>
      )}

      {hasRecommendations && (
        <AiSummaryRecommendationsView
          trainingPlan={summary.trainingPlan}
          nutrition={summary.nutrition}
          recommendations={summary.recommendations}
          variant="pdf"
        />
      )}

      {summary.disclaimer && (
        <section className={classes.disclaimer} data-pdf-break="after">
          <strong>Disclaimer</strong>
          <p>{summary.disclaimer}</p>
        </section>
      )}
    </>
  );
};

const AiSummaryPdfDocument = forwardRef<HTMLDivElement, Props>(
  (
    {
      kind,
      healthSummary,
      rmrSummary,
      rmrInBodySummary,
      amrSummary,
      progressSummary,
      bloodProgressSummary,
      style,
    },
    ref
  ) => (
    <div
      ref={ref}
      className={classes.document}
      style={style}
      data-ai-summary-pdf-document="true"
      aria-hidden="true"
    >
      {kind === "health_overview" && healthSummary && (
        <HealthOverviewPdf summary={healthSummary} />
      )}
      {kind === "rmr_summary" && rmrSummary && (
        <RmrSummaryPdf summary={rmrSummary} />
      )}
      {kind === "rmr_inbody_summary" && rmrInBodySummary && (
        <AiRmrInBodySummaryView summary={rmrInBodySummary} variant="pdf" />
      )}
      {kind === "amr_summary" && amrSummary && (
        <AmrSummaryPdf summary={amrSummary} />
      )}
      {kind === "progress_summary_1" && progressSummary && (
        <AiProgressSummaryView summary={progressSummary} variant="pdf" />
      )}
      {kind === "progress_summary_2" && bloodProgressSummary && (
        <AiBloodProgressSummaryView
          summary={bloodProgressSummary}
          variant="pdf"
        />
      )}
    </div>
  )
);

AiSummaryPdfDocument.displayName = "AiSummaryPdfDocument";

export default AiSummaryPdfDocument;
